import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router';
import { IS_PRODUCTION } from '../env';
import {
  Channel,
  channelsNewApi,
  Organisation,
  organisationApi,
  User,
  userApi,
  UserInfo,
  userInfoApi,
} from '../services';
import {
  CookieActionType,
  cookieOptions,
  CUSTOM_CLIENT_CONFIG,
  getChannelDomain,
  getWebAppUrl,
  MZ_ACCOUNT_COOKIE_KEY,
  useAppDispatch,
  useLoginRedirect,
} from '../shared';
import { getChannels } from '../slices';

interface UseLayoutDataArgs {
  excludeArchivedChannels: boolean;
}

interface LayoutData {
  userProfile: User;
  userInfo: UserInfo;
  channelBySubdomain: Channel;
  organisation: Organisation;
}

export const useLayoutData = ({ excludeArchivedChannels }: UseLayoutDataArgs) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { storeLoginRedirect } = useLoginRedirect();

  const [, setCookie] = useCookies([MZ_ACCOUNT_COOKIE_KEY]);

  const [data, setData] = useState<LayoutData>();

  const getUserData = useCallback(async () => {
    if (!IS_PRODUCTION) {
      return { userProfile: (await dispatch(userApi.endpoints.me.initiate())).data as User };
    }

    const userLoginByAnonymousOrGetCurrentProfileResponse = await dispatch(
      userApi.endpoints.userLoginByAnonymousOrGetCurrentProfile.initiate()
    );

    if ('error' in userLoginByAnonymousOrGetCurrentProfileResponse) {
      return {};
    }

    const { user: userProfile, accessToken } = {
      ...userLoginByAnonymousOrGetCurrentProfileResponse.data,
    };

    return { userProfile, accessToken };
  }, [dispatch]);

  useEffect(() => {
    const getData = async () => {
      try {
        const { userProfile, accessToken } = await getUserData();

        if (accessToken && CUSTOM_CLIENT_CONFIG?.baseHost) {
          setCookie(
            MZ_ACCOUNT_COOKIE_KEY,
            accessToken,
            cookieOptions({
              action: CookieActionType.SET,
              domain: `.${CUSTOM_CLIENT_CONFIG.baseHost}`,
            })
          );
        }

        if (!userProfile) {
          storeLoginRedirect();
          navigate('/auth/login');
          return null;
        }

        const channels = await dispatch(
          getChannels({ filters: { excludeArchived: excludeArchivedChannels } })
        ).unwrap();

        if (!channels || !channels.length) {
          navigate('/noAccess');
          return null;
        }

        const subdomain = getChannelDomain();

        if (!subdomain) {
          window.location.replace(getWebAppUrl(channels[0].subdomain));
          return null;
        }

        const { data: channelBySubdomain } = await dispatch(
          channelsNewApi.endpoints.channelBySubdomain.initiate({ subdomain })
        );

        if (!channelBySubdomain) {
          navigate('/noAccess');
          return null;
        }

        const { data: userInfo } = await dispatch(userInfoApi.endpoints.getUserInfo.initiate());

        if (!userInfo) {
          navigate('/noAccess');
          return null;
        }

        const { data: organisation } = await dispatch(
          organisationApi.endpoints.organisation.initiate()
        );

        if (!organisation) {
          navigate('/noAccess');
          return null;
        }

        setData({ userProfile, userInfo, channelBySubdomain, organisation });
      } catch (e) {
        console.log(e);
      }
    };

    getData();
  }, [dispatch, excludeArchivedChannels, getUserData, navigate, setCookie, storeLoginRedirect]);

  return { data };
};
